import { useLayoutEffect, useState, type RefObject } from 'react';

import { DEFAULT_MAX_RATIO, DEFAULT_MIN_HEIGHT } from 'components/docked/BottomSheet';

/**
 * Mede o conteúdo do body do `BottomSheet` (via ref) e retorna a altura
 * total ideal — incluindo paddings e gaps lidos do `getComputedStyle` — clamped
 * por `minHeight` e `maxRatio` da viewport.
 *
 * Se `measureRefs` for omitido, mede os children diretos do body. Use
 * `measureRefs` quando o body tiver children com `flex: 1` (que esmagam o
 * conteúdo e impedem leitura do `scrollHeight` natural) — passe refs para
 * elementos internos de tamanho natural.
 */
export function useBottomSheetHeight(
    bodyRef: RefObject<HTMLElement | null>,
    measureRefs?: (HTMLElement | null)[],
    minHeight = DEFAULT_MIN_HEIGHT,
    maxRatio = DEFAULT_MAX_RATIO,
) {
    const [height, setHeight] = useState(minHeight);

    useLayoutEffect(() => {
        const body = bodyRef.current;
        if (!body) return;

        const compute = () => {
            const cs = getComputedStyle(body);
            const padTop = parseFloat(cs.paddingTop) || 0;
            const padBottom = parseFloat(cs.paddingBottom) || 0;
            const gap = parseFloat(cs.rowGap || cs.gap) || 0;
            const fallback = Array.from(body.children) as HTMLElement[];
            const items = (measureRefs ?? fallback).filter((el): el is HTMLElement => !!el);
            const sum = items.reduce((s, el) => s + Math.max(el.scrollHeight, Math.ceil(el.getBoundingClientRect().height)), 0);
            const extras = padTop + padBottom + gap * Math.max(0, items.length - 1);
            const viewportMax = Math.round(window.innerHeight * maxRatio);

            setHeight(Math.max(minHeight, Math.min(Math.ceil(sum + extras), viewportMax)));
        };

        const ro = new ResizeObserver(compute);

        ro.observe(body);

        const targets = (measureRefs ?? (Array.from(body.children) as HTMLElement[])).filter((el): el is HTMLElement => !!el);
        targets.forEach((el) => ro.observe(el));

        compute();

        return () => ro.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bodyRef, minHeight, maxRatio, ...(measureRefs ?? [])]);

    return height;
}
